import { Fragment, useState, useEffect, useLayoutEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { flushSync } from 'react-dom';
import { useAutoLogout } from './hooks/useAutoLogout';
import useEsMovil from './hooks/useEsMovil';
import { fijarEnrutador, alCambiarUbicacion, navegarFuera, retrocederEnCola, hayCapasAbiertas, useCapa } from './utils/capas';
import {
  supabase,
  signOut, getMyProfile, onAuthChange, getSession, nivelMfa, factoresTotp,
  getCorrelativo, setCorrelativo as saveCorrelativo,
  getCorrContrasena, setCorrContrasena,
  getRecords, insertRecord, updateRecord, deleteRecord,
  getCatalog, saveCatalog, getCatOrder, saveCatOrder,
  getDoctors, getCabecerasResultados, soloCabecera, getQuotations, getPasswords,
  getInstituciones,                                     // Etapa 2: convenios
  insertPassword,
  getAllDistributionLogs,                               // Fix C
  resolverOCrearPaciente, buscarPacientePorDpi,         // Paso 3: identidad de paciente
  marcarPreorden, vincularPacienteLab,                                       // Fase 4.1A: write-back de preórdenes
  getMisPermisos,                                       // Fase 2: permisos por perfil (secretaria)
  getMisModulos,                                        // Módulos activos por clínica
} from './supabase';

import {
  C, g, TABS, SidebarIcons,
  esStaff,
  toGT, nextCorr, fmtGTDateTime,
  nextCorrContrasena, formatCorrContrasena,
} from './constants';

import { conTimeout, TIMEOUT_MS, TIMEOUT_CARGA_MS, MSG_TIMEOUT } from './utils/red';
import { ENTRADA, entradaPermite, entradaCorrecta } from './entrada';
import { MODULOS, puedeEntrar, moduloInicial, entradaMenu, rutaDe, moduloDeRuta, leerRuta } from './modulos';
import { useGuardiaSalida, guardiaVigente, saleDeGuardia, puedeSalirA } from './utils/guardiaSalida';
import SinAcceso from './components/SinAcceso';

import { InnovaLogo, BarcodeCanvas, printBarcode, CorrBar } from './components/UI';
import Sidebar      from './components/Sidebar';
import LimiteCarga  from './components/LimiteCarga';
import Login        from './components/Login';
import RecoveryScreen      from './components/RecoveryScreen';
import ForceChangePassword from './components/ForceChangePassword';
import MfaCodigo           from './components/MfaCodigo';
import { esc } from './utils/html';
import { MSG_SESION_VENCIDA } from './utils/claveAuth';

// ── Carga diferida de pantallas ──────────────────────────────────────────────
// Las pantallas de cada módulo se declaran en src/modulos.js (registro único).
// Aquí quedan solo las que App.js pinta por fuera del registro: los pasos de
// Nueva orden y la consulta del médico, que se mantiene montada aunque el
// médico se mueva por el menú.
// Lo que NO se difiere: Sidebar, Login y UI — se necesitan en el primer pintado
// y diferirlos solo agregaría un parpadeo.

const TabPaciente = lazy(() => import('./tabs/TabPaciente'));
const TabExamenes = lazy(() => import('./tabs/TabExamenes'));
const TabMuestra = lazy(() => import('./tabs/TabMuestra'));
const TabMedico = lazy(() => import('./tabs/TabMedico'));
const TabFacturacion = lazy(() => import('./tabs/TabFacturacion'));
const TabQBOrdenes = lazy(() => import('./tabs/TabQBOrdenes'));
const TabWorkspaceMedico = lazy(() => import('./tabs/TabWorkspaceMedico'));
const EMPTY_PACIENTE = { nombre:'', apellido:'', nacimiento:'', edad:'', telefono:'', sexo:'', dpi:'', nit:'', habitacion:'N/A' };
const EMPTY_MUESTRA  = { fecha:'', hora:'12', minuto:'00', ampm:'AM', tipos:[], tipoOtro:'', lugar:'', lugarOtro:'', responsable:'', responsableOtro:'', responsable_tipo:'', responsable_id:'', bitacora:'' };
const EMPTY_MEDICO   = { nombre:'', apellido:'', telefono:'', correo:'', colegiado:'', observaciones:'', codigo:'', descuento:0 };
const EMPTY_FACTURA  = { metodo:'Efectivo', comentarios:'' };

// ¿El operador ya capturó algo en esta sección de Nueva orden? Vacío o igual al
// valor de fábrica no cuenta (la hora '12' del paso Muestra, por ejemplo).
const esVacio = (v) => v === '' || v == null || (Array.isArray(v) && v.length === 0);
const seccionTocada = (data, base) => Object.keys({ ...base, ...data }).some((k) => {
  const v = data?.[k];
  if (esVacio(v) && esVacio(base[k])) return false;
  return JSON.stringify(v) !== JSON.stringify(base[k]);
});
const MSG_SALIR_ORDEN = 'Tienes una orden sin guardar.\n\n¿Salir de Nueva orden? Lo capturado se queda aquí mientras no recargues ni cierres la página.';

// NOTA Etapa 2: la habitación quedó como dato INFORMATIVO del paciente.
// El descuento ya NO depende de la cama (vive en la institución de convenio) y
// la contraseña ya NO depende de la cama (se genera solo cuando el método es "Crédito").

// ── Fase 3 ───────────────────────────────────────────────────────────────────
// Mapea el perfil operativo (tabla profiles) al shape legacy que consume la app.
// CRÍTICO: profiles.nombre → user.name. Toda la app estampa user.name / user.username
// (saved_by, created_by, cabecera, firma/sello/colegiado del QB). No renombrar.
function mapProfile(p, authUser) {
  if (!p) return null;
  return {
    id:             p.id,
    username:       p.username,
    name:           p.nombre,          // ← mapeo clave
    role:           p.role,
    activo:         p.activo,
    sede_id:        p.sede_id,
    institucion_id: p.institucion_id,
    doctor_id:      p.doctor_id,
    firma_url:      p.firma_url,
    sello_url:      p.sello_url,
    no_colegiado:   p.no_colegiado,
    email:          authUser?.email ?? null,
  };
}

// Traslape del poll incremental: una escritura que hizo commit tarde (su sello
// es la hora en que EMPEZÓ su transacción) igual entra en la siguiente vuelta.
const TRASLAPE_POLL_MS = 120000;

// ¿Cambió algo que la pantalla muestra? Evita re-pintar si el poll trae lo mismo.
const mismaCabecera = (a, b) => a.sync_at === b.sync_at && a.estado === b.estado
  && a.n_valores === b.n_valores && a.n_llenos === b.n_llenos
  && a.n_pendientes === b.n_pendientes && a.n_distribuciones === b.n_distribuciones;

// Los títulos de cada módulo viven en src/modulos.js (MODULOS[id].titulo).

export default function App() {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Resiliencia de arranque ────────────────────────────────────────────────
  // Cada pantalla de espera necesita una salida. Sin esto, una caída del
  // servicio deja al usuario mirando un splash que no cambia nunca.
  const [loadError,     setLoadError]     = useState('');   // falló la carga de datos
  const [perfilError,   setPerfilError]   = useState('');   // falló resolver el perfil
  const [authCaido,     setAuthCaido]     = useState(false);// Auth no contestó al arrancar
  const [intentoPerfil, setIntentoPerfil] = useState(0);    // contador de reintentos
  // Verificación en dos pasos (Lote B2): 'revisando' | 'ok' | 'pide' | 'error' | 'sin_sesion'.
  // Con 'pide' no se carga ni el perfil: primero el código, después el portal.
  const [mfa, setMfa] = useState({ estado: 'revisando', factorId: null });
  const [intentoMfa, setIntentoMfa] = useState(0);

  // ── Auth (Fase 3) ──
  const [session,      setSession]      = useState(null);
  const [authReady,    setAuthReady]    = useState(false);
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [mustChange,   setMustChange]   = useState(false);
  const [wsPacienteId, setWsPacienteId] = useState(null); // anclaje del workspace de consulta
  // Nombre del paciente en foco: solo para rotular el acceso "Consulta en curso"
  // del sidebar. Lo reporta el propio workspace cuando termina de cargar.
  const [wsPacienteNombre, setWsPacienteNombre] = useState('');
  // La consulta sigue "abierta" aunque el médico esté viendo otro módulo:
  // es lo que mantiene el workspace montado (escondido) en vez de destruido.
  const [wsAbierto, setWsAbierto] = useState(false);

  // ── Dirección web = módulo pedido (etapa 1 del enrutador) ──────────────────
  // La URL es la fuente de verdad: `activeModule` se deriva de la dirección
  // (null en "/" o en una ruta desconocida). `setActiveModule` conserva su firma
  // —las pantallas le pasan un id de módulo— y por dentro navega a la ruta del
  // registro, así que atrás, adelante y recargar funcionan sin tocar pestañas.
  // Qué se PINTA lo sigue decidiendo `modulo`, más abajo, con los candados.
  const location     = useLocation();
  const navigate     = useNavigate();
  const tipoNavegacion = useNavigationType();
  // Etapa 3: una dirección también puede traer el id de UN registro
  // (`/pacientes/:id`, `/consulta/:id`). El registro declara cuáles (`conId`).
  //
  // Guardia de salida (etapa 3b, src/utils/guardiaSalida.js): si una pantalla
  // tiene datos sin guardar (Nueva orden, Ingreso de resultados) y llega un
  // atrás/adelante que sale de su dirección, se SIGUE PINTANDO la dirección
  // protegida mientras se pregunta. Se decide aquí, al pintar, porque esos datos
  // viven dentro de la pantalla: si se desmontara antes de preguntar, ya se
  // habrían perdido. Si el usuario confirma, se pinta la dirección real; si no,
  // se deshace el salto (efecto de más abajo).
  const [salidaPermitida, setSalidaPermitida] = useState(null);   // location.key ya confirmada
  const guardia      = guardiaVigente();
  const rutaRetenida = guardia && tipoNavegacion === 'POP' && salidaPermitida !== location.key
    && saleDeGuardia(location.pathname) ? guardia.ruta : null;
  const rutaLeida    = leerRuta(rutaRetenida || location.pathname);
  const activeModule = rutaLeida.modulo;
  // Refs para que setActiveModule sea estable (lo usan callbacks con deps []).
  const navegarRef   = useRef(navigate);
  navegarRef.current = navigate;
  const rutaActual   = useRef(location.pathname);
  rutaActual.current = location.pathname;
  const ubicacionRef = useRef(location);
  ubicacionRef.current = location;
  // Paciente de la consulta abierta, para volver a /consulta/:id desde el menú.
  const wsIdRef      = useRef(null);
  wsIdRef.current    = wsPacienteId;
  // Capas (etapa 2): "atrás" cierra lo que está encima. Se fija en el render
  // para que una capa que se desmonta por un cambio de pantalla ya vea la
  // ubicación nueva (ver src/utils/capas.js).
  fijarEnrutador(location, navigate);
  useLayoutEffect(() => {
    alCambiarUbicacion(location, tipoNavegacion);
  }, [location.key]);   // eslint-disable-line react-hooks/exhaustive-deps
  const setActiveModule = useCallback((id, { reemplazar = false, sinConfirmar = false, registroId } = {}) => {
    // La consulta vuelve a SU dirección (/consulta/:id) aunque se llegue desde
    // el menú («Consulta en curso»), no a /consulta a secas.
    const reg = registroId !== undefined ? registroId
      : id === 'medico-workspace' ? wsIdRef.current : null;
    const destino = rutaDe(id, reg);
    if (destino === rutaActual.current) return;   // ya está ahí: no apila otra entrada
    // Con datos sin guardar (Nueva orden, Ingreso de resultados) pregunta antes.
    if (!sinConfirmar && !puedeSalirA(destino)) return;
    // Con capas abiertas (menú, hojas) sus entradas se sustituyen por la del
    // destino: no queda una capa huérfana en el historial.
    navegarFuera(destino, { replace: reemplazar });
  }, []);
  // Corta el render de la app mientras se cierra la sesión. Sin esto, mover la
  // dirección a "/" repinta el portal con la sesión todavía viva: un médico o
  // una secretaria veían un pantallazo de un módulo que no es suyo antes de
  // caer al login.
  const [saliendo, setSaliendo] = useState(false);
  const saliendoRef     = useRef(false);   // salida en curso: su SIGNED_OUT no es «sesión vencida»
  const sesionViva      = useRef(false);   // hubo sesión en esta pestaña
  const handleLogoutRef = useRef(null);    // el listener de Auth se arma antes que handleLogout
  const [avisoSesion, setAvisoSesion] = useState('');

  // "Nueva cita" del sidebar: navega a Mi Clínica Y abre el modal de nueva cita.
  // Timestamp como trigger; se consume (→0) al abrir el modal para no re-disparar.
  const [nuevaCitaTrigger, setNuevaCitaTrigger] = useState(0);

  // Teléfono: el menú lateral pasa a ser un panel que se abre con ☰.
  const esMovil = useEsMovil();
  const [menuAbierto, setMenuAbierto] = useState(false);
  useEffect(() => { if (!esMovil) setMenuAbierto(false); }, [esMovil]);
  // El menú abierto es una capa: "atrás" lo cierra.
  useCapa(esMovil && menuAbierto, () => setMenuAbierto(false));
  // Con el panel abierto, la página de atrás no se desplaza con el dedo.
  useEffect(() => {
    if (!menuAbierto) return undefined;
    const previo = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previo; };
  }, [menuAbierto]);


  // El aterrizaje por rol (médico → Mi Clínica, secretaria → Inicio, manager →
  // Indicadores) ya NO se hace con un efecto: vive en src/modulos.js y se resuelve
  // abajo, en el render. Un efecto corría después del primer pintado y en ese
  // cuadro el portal externo alcanzaba a ver 'registros', que es de operaciones.

  // Permisos del perfil (catálogo `permisos_catalogo` + overrides por usuario).
  // Decide qué se MUESTRA; la RLS sigue decidiendo qué se puede hacer. Se resuelve
  // con la misma función que evalúan las policies, así que no pueden discrepar.
  const [permisos, setPermisos] = useState({});
  // De qué usuario son los permisos cargados. Mientras no coincida con el
  // usuario actual, el menú y el candado de módulos todavía no pueden decidir.
  const [permisosDe, setPermisosDe] = useState(null);
  useEffect(() => {
    let vivo = true;
    if (!user) { setPermisos({}); setPermisosDe(null); return; }
    getMisPermisos()
      .then((p) => { if (vivo) { setPermisos(p || {}); setPermisosDe(user.id); } })
      .catch((e) => { console.error('getMisPermisos falló:', e); if (vivo) { setPermisos({}); setPermisosDe(user.id); } });
    return () => { vivo = false; };
  }, [user?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Módulos que usa la clínica del usuario. Decide qué entradas EXISTEN en el
  // menú: permisos responde "¿este usuario puede?", módulos responde "¿esta
  // clínica trabaja así?". Son preguntas distintas y ninguna reemplaza a la otra.
  const [modulos, setModulos] = useState({});
  const [modulosDe, setModulosDe] = useState(null);
  useEffect(() => {
    let vivo = true;
    if (!user) { setModulos({}); setModulosDe(null); return; }
    getMisModulos()
      .then((m) => { if (vivo) { setModulos(m || {}); setModulosDe(user.id); } })
      .catch((e) => { console.error('getMisModulos falló:', e); if (vivo) { setModulos({}); setModulosDe(user.id); } });
    return () => { vivo = false; };
  }, [user?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  const [tab, setTab] = useState('paciente');

  const [corr,           setCorr]           = useState('0001');
  const [corrContrasena, setCorrContrasena2] = useState('C-0001');
  const [records,        setRecords]         = useState([]);
  const [doctors,        setDoctors]         = useState([]);
  const [results,        setResults]         = useState([]);
  const [quotations,     setQuotations]      = useState([]);
  const [passwords,      setPasswords]       = useState([]);
  const [distributionLogs, setDistributionLogs] = useState([]); // Fix C

  const [paciente,     setPaciente]     = useState(EMPTY_PACIENTE);
  const [examenes,     setExamenes]     = useState([]);
  const [muestra,      setMuestra]      = useState(EMPTY_MUESTRA);
  const [medico,       setMedico]       = useState(EMPTY_MEDICO);
  const [factura,      setFactura]      = useState(EMPTY_FACTURA);
  const [esEmergencia, setEsEmergencia] = useState(false);

  const [instituciones,  setInstituciones]  = useState([]);   // instituciones activas (selector filtra convenios)
  const [institucionSel, setInstitucionSel] = useState(null); // institución de convenio seleccionada (o null)
  const [preordenActiva, setPreordenActiva] = useState(null); // Fase 4.1A: {id, codigo} de la solicitud que origina la orden
  const [sharedCatalog,  setSharedCatalog]  = useState(null);
  const [sharedCatOrder, setSharedCatOrder] = useState(null);

  const CARGO_EMERGENCIA = 150;

  // ── Descuento de CONVENIO: ÚNICA fuente de verdad del precio ─────────────────
  // Vive en la institución seleccionada (records.institucion_id). Reemplaza por
  // completo la lógica de cama; nunca se acumulan (cama = solo informativa).
  //
  // El neto se deriva POR LÍNEA con el pct ACTUAL — NO se confía en examenes[].price,
  // que queda stale a precio lleno: los exámenes se cotizan en el paso 2 (pct=0) y la
  // institución se elige hasta el paso 4, cuando TabExamenes (y su useEffect que
  // repreciaba) ya está desmontado. Esta derivación es IDÉNTICA, línea por línea y con
  // el mismo redondeo, a la que usa la contraseña en saveOrder → total mostrado,
  // registro guardado y contraseña coinciden al centavo.
  //
  // INGRESO POR CLÍNICA DE EMERGENCIA → SIN DESCUENTO, siempre.
  // Regla original de addbb60 (05-may-2026), que vivía en
  //   HAB_CON_DESCUENTO = hab => hab && hab !== 'N/A' && hab !== 'Clínica de Emergencia'
  // y se perdió sin querer en 28039f7 (14-jun-2026) al migrar el descuento de cama →
  // institución de convenio: se borró el check de habitación y con él la exclusión.
  // NO confundir con `esEmergencia` (radio 🚨 → cargo de Q150): ese cargo lo rige la
  // política de operación y es INDEPENDIENTE de esta regla. Aquí solo importa el
  // lugar de ingreso, que sigue viviendo en paciente.habitacion.
  const esClinicaEmergencia = paciente.habitacion === 'Clínica de Emergencia';
  const descConvenioPct  = (institucionSel && !esClinicaEmergencia)
    ? (Number(institucionSel.descuento) || 0)
    : 0;
  const hasDescuento     = descConvenioPct > 0;
  const convFactor       = 1 - (descConvenioPct / 100);   // 1 = sin descuento

  // Neto por línea = precioOriginal × factor, redondeado a 2 decimales POR LÍNEA.
  // Se basa siempre en precioOriginal (precio de lista real), nunca en price (posible stale).
  const examenesNeto = examenes.map(e => {
    const precioBase = parseFloat((e.precioOriginal ?? e.price).toFixed(2));
    return { ...e, precioOriginal: precioBase, price: parseFloat((precioBase * convFactor).toFixed(2)) };
  });

  // La contraseña es el documento de cobro del sanatorio → solo cuando el método es "Crédito".
  const generaContrasena = factura.metodo === 'Crédito';

  const totalBruto  = parseFloat(examenes.reduce((a, e) => a + (e.precioOriginal ?? e.price), 0).toFixed(2));
  const totalNeto   = parseFloat(examenesNeto.reduce((a, e) => a + e.price, 0).toFixed(2)); // suma de netos ya redondeados por línea
  const descuento   = parseFloat((totalBruto - totalNeto).toFixed(2));
  const total       = totalBruto;
  const totalFinal  = parseFloat((totalNeto + (esEmergencia ? CARGO_EMERGENCIA : 0)).toFixed(2));

  // ── Sincronización de resultados (24-oct-2026) ─────────────────────────
  // En memoria solo viven CABECERAS con conteos (sin valores): los valores de
  // un resultado se piden al abrirlo o al sacar su PDF. El poll ya no baja
  // todas las cabeceras: pide a la base solo lo que cambió desde el último
  // cursor (con traslape), así que su costo no depende del historial.
  const cursorResultados = useRef(null);     // `ahora` del servidor en la última consulta

  const mezclarCabeceras = useCallback((filas) => {
    if (!filas?.length) return;
    setResults(prev => {
      const nuevas = new Map(filas.map(f => [f.correlativo, soloCabecera(f)]));
      let cambio = false;
      const sig = (prev || []).map(r => {
        const n = nuevas.get(r.correlativo);
        if (!n) return r;
        nuevas.delete(r.correlativo);
        if (mismaCabecera(r, n)) return r;
        cambio = true;
        return n;
      });
      if (nuevas.size) return [...nuevas.values(), ...sig];
      return cambio ? sig : prev;            // nada cambió: no re-pintar
    });
  }, []);

  const sincronizarResultados = useCallback(async () => {
    const cursor = cursorResultados.current;
    if (!cursor) return;
    const desde = new Date(Date.parse(cursor) - TRASLAPE_POLL_MS).toISOString();
    const { ahora, filas } = await getCabecerasResultados({ desde });
    mezclarCabeceras(filas);
    if (ahora) cursorResultados.current = ahora;
  }, [mezclarCabeceras]);

  const loadAll = useCallback(async () => {
    setLoading(true); setLoadError('');
    try {
      // conTimeout sobre el Promise.all: si una sola de estas llamadas queda
      // colgada, el Promise.all no resuelve nunca y `setLoading(false)` jamás
      // corre → splash "CONECTANDO CON LA BASE DE DATOS..." eterno.
      const [cr, rc, cat, corder, docs, res, quot, pass, corrC, distLogs, inst] = await conTimeout(Promise.all([
        getCorrelativo(), getRecords(), getCatalog(), getCatOrder(),
        getDoctors(), getCabecerasResultados(), getQuotations(), getPasswords(), getCorrContrasena(),
        getAllDistributionLogs(),                                             // Fix C
        getInstituciones(),                                                   // Etapa 2
      ]), 'carga inicial de datos', TIMEOUT_CARGA_MS);
      setCorr(cr || '0001');
      setRecords(rc || []);
      if (cat)    setSharedCatalog(cat);
      if (corder) setSharedCatOrder(corder);
      setDoctors(docs || []);
      setResults(res?.filas || []);
      cursorResultados.current = res?.ahora || null;
      setQuotations(quot || []);
      setPasswords(pass || []);
      setCorrContrasena2(corrC || 'C-0001');
      setDistributionLogs(distLogs || []);                                   // Fix C
      setInstituciones(inst || []);                                          // Etapa 2
    } catch(e) {
      console.error('Load error', e);
      setLoadError(e?.esTimeout ? MSG_TIMEOUT : 'No se pudieron cargar los datos. Revisa tu conexión y vuelve a intentar.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Carga inicial de datos: solo tras autenticación (RLS depende de la sesión)
  // y solo si el usuario entró por la puerta que le corresponde. Un médico que
  // abre operaciones no descarga nada del laboratorio: ve el aviso y se va.
  useEffect(() => { if (user && entradaPermite(user.role)) loadAll(); }, [user, loadAll]);

  // ── Bootstrap de sesión (Fase 3) ───────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthChange((event, sess) => {
      // Enlace de recuperación abierto → pantalla "nueva contraseña".
      if (event === 'PASSWORD_RECOVERY') { setRecoveryMode(true); setAuthReady(true); return; }
      // Sesión vencida en el servidor (Lote B2): el refresh falla y supabase-js
      // emite SIGNED_OUT (o TOKEN_REFRESHED sin sesión). Si no lo pidió el
      // usuario, se sale por el mismo camino que «Cerrar sesión», que tapa la
      // pantalla y vacía los datos antes de mover la dirección.
      const perdida = (event === 'SIGNED_OUT' || (event === 'TOKEN_REFRESHED' && !sess));
      if (perdida && sesionViva.current && !saliendoRef.current) {
        sesionViva.current = false;
        setRecoveryMode(false);
        handleLogoutRef.current?.({ aviso: MSG_SESION_VENCIDA });
        setAuthReady(true);
        return;
      }
      if (event === 'SIGNED_OUT')        { setRecoveryMode(false); }
      if (event === 'SIGNED_IN')         { setAvisoSesion(''); }
      sesionViva.current = !!sess;
      setSession(sess);
      setAuthReady(true);
    });
    // Sesión inicial por si INITIAL_SESSION no dispara.
    getSession()
      .then(s => { if (s) sesionViva.current = true; setSession(s); setAuthReady(true); })
      .catch(e => { console.error('getSession', e); setAuthReady(true); setAuthCaido(true); });
    return () => { unsub?.(); };
  }, []);

  // Red de seguridad del arranque: si Auth no contesta en 15s, liberamos la
  // pantalla igual. Sin sesión el usuario cae al login —que sí es accionable—
  // con un aviso de que el servicio está fallando, en vez de un splash muerto.
  useEffect(() => {
    if (authReady) return;
    const t = setTimeout(() => { setAuthReady(true); setAuthCaido(true); }, TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [authReady]);

  // Nivel de la sesión (Lote B2). Corre al entrar, al refrescar el token y al
  // verificar el código (MFA_CHALLENGE_VERIFIED trae un token aal2). Se lee del
  // token y del usuario en memoria: no hace llamadas de red.
  useEffect(() => {
    if (recoveryMode) return;
    if (!session) { setMfa({ estado: 'sin_sesion', factorId: null }); return; }
    let cancel = false;
    setMfa(m => (m.estado === 'ok' ? m : { estado: 'revisando', factorId: null }));
    (async () => {
      try {
        const { actual, siguiente } = await nivelMfa();
        if (siguiente === 'aal2' && actual !== 'aal2') {
          const { verificados } = await factoresTotp();
          if (cancel) return;
          if (verificados.length) { setMfa({ estado: 'pide', factorId: verificados[0].id }); return; }
        }
        if (!cancel) setMfa({ estado: 'ok', factorId: null });
      } catch (e) {
        console.error('nivelMfa', e);
        // Se cierra: sin saber el nivel no se deja pasar a quien podría tener código.
        if (!cancel) setMfa({ estado: 'error', factorId: null });
      }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.access_token, recoveryMode, intentoMfa]);

  // Resolver el perfil operativo cuando hay sesión (y no estamos en recovery).
  useEffect(() => {
    if (recoveryMode) return;
    const au = session?.user;
    if (!au) { setUser(null); setMustChange(false); setPerfilError(''); return; }
    if (mfa.estado !== 'ok') return;   // primero el código de verificación (Lote B2)
    setMustChange(!!au.user_metadata?.must_change_password);
    setPerfilError('');
    let cancel = false;
    conTimeout(getMyProfile(), 'perfil de usuario').then(async (profile) => {
      if (cancel) return;
      if (!profile) { await signOut(); return; }               // sin perfil → fuera
      if (profile.activo === false) {                          // inactivo → fuera
        alert('Tu cuenta está inactiva. Contacta a un administrador.');
        await signOut(); return;
      }
      setUser(mapProfile(profile, au));
    }).catch(e => {
      if (cancel) return;
      console.error('Perfil', e);
      // Sin este catch, un rechazo dejaba `user` en null y la pantalla clavada
      // en "CARGANDO PERFIL..." sin ningún mensaje.
      setPerfilError(e?.esTimeout ? MSG_TIMEOUT : 'No pudimos cargar tu perfil. Vuelve a intentar.');
    });
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id, session?.user?.user_metadata?.must_change_password, recoveryMode, intentoPerfil, mfa.estado]);

  useEffect(() => {
    if (!user) return;   // Fase 3: suscribir canales solo autenticado (RLS).
    const chSettings = supabase
      .channel('settings-sync')
      .on('postgres_changes', { event:'*', schema:'public', table:'settings' }, (payload) => {
        const k = payload.new?.key || payload.old?.key;
        if (k === 'correlativo')            setCorr(payload.new.value);
        if (k === 'correlativo_contrasena') setCorrContrasena2(payload.new.value);
        if (k === 'catalog')  { try { setSharedCatalog(JSON.parse(payload.new.value)); } catch {} }
        if (k === 'catOrder') { try { setSharedCatOrder(JSON.parse(payload.new.value)); } catch {} }
      })
      .subscribe();

    const chRecords = supabase
      .channel('records-sync')
      .on('postgres_changes', { event:'INSERT', schema:'public', table:'records' }, (payload) => {
        if (payload.new) {
          setRecords(prev => {
            if (prev.some(r => r.correlativo === payload.new.correlativo)) return prev;
            return [payload.new, ...prev];
          });
        }
      })
      .on('postgres_changes', { event:'UPDATE', schema:'public', table:'records' }, (payload) => {
        if (payload.new) setRecords(prev => prev.map(r => r.correlativo === payload.new.correlativo ? { ...r, ...payload.new } : r));
      })
      .on('postgres_changes', { event:'DELETE', schema:'public', table:'records' }, (payload) => {
        if (payload.old?.correlativo) {
          setRecords(prev => prev.filter(r => r.correlativo !== payload.old.correlativo));
          // La base borra en cascada la contraseña de la orden (FK comprobantes_credito.orden_corr)
          setPasswords(prev => prev.filter(p => p.orden_corr !== payload.old.correlativo));
        }
      })
      .subscribe();

    const chResults = supabase
      .channel('results-sync')
      .on('postgres_changes', { event:'*', schema:'public', table:'results' }, (payload) => {
        // El aviso trae la fila de results (con la columna `valores` deprecated,
        // que se descarta). Los conteos se piden con unos segundos de margen:
        // los valores se borran y se reinsertan después de la cabecera.
        const nuevo = soloCabecera(payload.new);
        const corr  = nuevo?.correlativo;
        if (!corr) return;
        if (payload.eventType === 'INSERT') {
          setResults(prev => [nuevo, ...prev.filter(r => r.correlativo !== corr)]);
        } else if (payload.eventType === 'UPDATE') {
          setResults(prev => prev.map(r => r.correlativo === corr ? { ...r, ...nuevo } : r));
        }
        setTimeout(() => {
          getCabecerasResultados({ correlativos: [corr] })
            .then(({ filas }) => mezclarCabeceras(filas))
            .catch(() => {});
        }, 3000);
      })
      .subscribe();

    const chDoctors = supabase
      .channel('doctors-sync')
      .on('postgres_changes', { event:'*', schema:'public', table:'doctors' }, () => {
        getDoctors().then(d => setDoctors(d || []));
      })
      .subscribe();

    const chInstituciones = supabase
      .channel('instituciones-sync')
      .on('postgres_changes', { event:'*', schema:'public', table:'instituciones' }, () => {
        getInstituciones().then(i => setInstituciones(i || []));
      })
      .subscribe();

    // Los canales de realtime de arriba (chSettings/chRecords/chResults) ya
    // empujan cada cambio en el momento en que ocurre. Estos polls son solo red
    // de seguridad por si el websocket se cae: van lentos y solo corren con la
    // pestana visible, asi que una pestana olvidada en segundo plano no consulta
    // nada. Mismo patron de document.hidden que ya usa TabPreordenes.js.
    // (27-ago-2026: estaban en 8s/10s/15s sin guard y saturaban la instancia
    //  compartida hasta tumbar el servicio de Auth.)
    const pollVisible = () => !document.hidden;

    const pollCorr = setInterval(async () => {
      if (!pollVisible()) return;
      try { const cr = await getCorrelativo(); if (cr) setCorr(cr); } catch {}
      try { const cc = await getCorrContrasena(); if (cc) setCorrContrasena2(cc); } catch {}
      try { const cat = await getCatalog(); if (cat) setSharedCatalog(cat); } catch {}
      try { const co = await getCatOrder(); if (co) setSharedCatOrder(co); } catch {}
    }, 120000);

    const pollRecords = setInterval(async () => {
      if (!pollVisible()) return;
      try { const rc = await getRecords(); if (rc) setRecords(rc); } catch {}
    }, 60000);

    // FIX A: poll de results para todos los usuarios (red de seguridad, 60s).
    // Solo baja las cabeceras que cambiaron desde la última vuelta.
    const pollResults = setInterval(async () => {
      if (!pollVisible()) return;
      try { await sincronizarResultados(); } catch {}
    }, 60000);

    return () => {
      supabase.removeChannel(chSettings);
      supabase.removeChannel(chRecords);
      supabase.removeChannel(chResults);
      supabase.removeChannel(chDoctors);
      supabase.removeChannel(chInstituciones);
      clearInterval(pollCorr);
      clearInterval(pollRecords);
      clearInterval(pollResults);
    };
  }, [user?.id, sincronizarResultados, mezclarCabeceras]);

  // Ajuste manual del No. de orden (Configuración y barra de Nueva orden). Guarda
  // primero: si la base lo rechaza (retroceso o formato, Lote 6) devuelve el
  // mensaje y la pantalla conserva el número vigente.
  const handleSetCorr = async (nc) => {
    const err = await saveCorrelativo(nc);
    if (err) return err;
    setCorr(nc);
    return null;
  };

  // Refresca en pantalla el perfil de la sesión. La lista de usuarios ya no vive
  // acá: cada pantalla de administración la pide cuando la necesita.
  const updateLocalUser = (uid, updates) => {
    if (user?.id === uid) setUser(prev => ({ ...prev, ...updates }));
  };

  const resetForm = () => {
    setPaciente(EMPTY_PACIENTE);
    setExamenes([]);
    setMuestra(EMPTY_MUESTRA);
    setMedico(EMPTY_MEDICO);
    setFactura(EMPTY_FACTURA);
    setEsEmergencia(false);
    setInstitucionSel(null);
    setPreordenActiva(null);
  };

  // Limpieza por tab (cada botón "🧹 Limpiar" resetea solo su sección al EMPTY_* canónico)
  const clearPaciente = () => { setPaciente(EMPTY_PACIENTE); setEsEmergencia(false); };
  const clearMuestra  = () => setMuestra(EMPTY_MUESTRA);
  const clearMedico   = () => { setMedico(EMPTY_MEDICO); setInstitucionSel(null); };
  const clearFactura  = () => setFactura(EMPTY_FACTURA);

  const imprimirContrasena = (pw) => {
    // Descuento manual (Caja): los exámenes quedan en Q0 y se cobra el total como paquete.
    const esPaquete = (pw.examenes || []).length > 0
      && (pw.examenes || []).every(e => !Number(e.price))
      && Number(pw.total) > 0;
    const filas = (pw.examenes || []).map((e, i) => `
      <tr style="background:${i % 2 === 0 ? '#fff' : '#f8fafc'}">
        <td style="padding:8px 14px;font-weight:700;color:#0891b2;text-align:center">${i + 1}</td>
        <td style="padding:8px 14px">${esc(e.name)}</td>
        <td style="padding:8px 14px;color:#64748b;font-size:12px">${esc(e.cat)}</td>
        <td style="padding:8px 14px;text-align:right;font-weight:700;color:#0d2b45">Q${(e.price || 0).toFixed(2)}</td>
      </tr>`).join('');

    const filaEmergencia = pw.es_emergencia ? `
      <tr style="background:#fff1f2">
        <td style="padding:8px 14px;font-weight:700;color:#dc2626;text-align:center">🚨</td>
        <td style="padding:8px 14px;font-weight:700;color:#dc2626" colspan="2">Cargo por Emergencia</td>
        <td style="padding:8px 14px;text-align:right;font-weight:700;color:#dc2626">Q150.00</td>
      </tr>` : '';

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Contraseña ${esc(pw.correlativo)}</title>
    <style>*{margin:0;padding:0;box-sizing:border-box}@media print{*{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}}body{font-family:'Inter',Arial,sans-serif;background:#fff;color:#1e293b}.page{max-width:780px;margin:0 auto;padding:40px 48px}.header{display:flex;align-items:flex-start;justify-content:space-between;padding-bottom:20px;border-bottom:4px solid #0d2b45;margin-bottom:28px}.logo-area{display:flex;align-items:center;gap:10px}.logo-text{font-size:26px;font-weight:800;color:#0d2b45}.doc-title{font-size:22px;font-weight:800;color:#0d2b45;letter-spacing:1px}.doc-date{font-size:12px;color:#64748b;margin-top:4px;text-align:right}.px-box{background:#e0f2fe;border-left:5px solid #0891b2;border-radius:6px;padding:14px 18px;margin-bottom:24px}.px-label{font-size:10px;font-weight:700;color:#0891b2;letter-spacing:2px;margin-bottom:6px}.px-name{font-size:18px;font-weight:700;color:#0d2b45}.px-details{font-size:12px;color:#475569;margin-top:4px}.table-wrapper{border:1px solid #e2e8f0;border-radius:8px;overflow:hidden}table{width:100%;border-collapse:collapse}thead tr{background:#0d2b45}thead th{padding:10px 14px;text-align:left;font-size:13px;color:#fff;font-weight:700}thead th:last-child{text-align:right}.total-row{background:#0d2b45;display:flex;justify-content:space-between;padding:12px 14px}.total-label{font-size:13px;font-weight:700;color:#7fb3cc;letter-spacing:2px}.total-amount{font-size:15px;font-weight:800;color:#fff}.footer{margin-top:36px;padding-top:16px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between}.footer-text{font-size:10px;color:#94a3b8}.footer-slogan{font-size:11px;font-weight:600;color:#0d2b45;font-style:italic}.print-btn{display:block;margin:24px auto 0;padding:10px 32px;background:#0d2b45;color:#fff;border:none;border-radius:8px;cursor:pointer;font-size:14px;font-family:inherit}@media print{.print-btn{display:none}}</style></head><body>
    <div class="page"><div class="header"><div class="logo-area"><svg xmlns="http://www.w3.org/2000/svg" viewBox="181 564 1195 1200" style="height:44px;width:auto;display:block"><path fill="#0d2b45" d="M1376 564.5L1376 1161.7L778.7 564.5ZM181.7 564.5L778.7 1161.7L778.7 564.5ZM1376 1758.8L1376 1161.7L778.7 1161.7ZM778.7 1758.8L778.7 1161.7L181.7 1161.7Z"/></svg><span class="logo-text">Innovalab</span></div><div><div class="doc-title">Contraseña</div><div style="font-size:18px;font-weight:800;color:#0891b2;letter-spacing:1px;text-align:right;margin-top:2px">${esc(pw.correlativo)}</div><div class="doc-date">Fecha: ${new Date().toLocaleDateString('es-GT')}</div></div></div>
    <div class="px-box"><div class="px-label">Paciente</div><div class="px-name">${esc(pw.paciente)}</div><div class="px-details">${pw.institucion ? '🏢 ' + esc(pw.institucion) : esc(pw.habitacion)}${pw.es_emergencia ? ' · 🚨 EMERGENCIA' : ''}</div></div>
    <div class="table-wrapper"><table><thead><tr><th style="width:50px;text-align:center">#</th><th>Examen</th><th>Categoría</th><th style="text-align:right">Precio</th></tr></thead><tbody>${filas}${filaEmergencia}</tbody></table><div class="total-row"><span class="total-label">${esPaquete ? 'Total por todos los laboratorios' : 'Total a Cobrar'}</span><span class="total-amount">Q${(pw.total || 0).toFixed(2)}</span></div></div>
    <div class="footer"><span class="footer-text">Innovalab · ${new Date().toLocaleDateString('es-GT')}</span><span class="footer-slogan">Innovación en Diagnósticos</span></div></div>
    <button class="print-btn" onclick="window.print()">🖨 Imprimir / Guardar PDF</button></body></html>`;

    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const w    = window.open(url, '_blank');
    if (!w) {
      // Fallback si el browser bloquea el popup
      const a = document.createElement('a');
      a.href = url; a.target = '_blank'; a.rel = 'noopener noreferrer';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    }
    setTimeout(() => URL.revokeObjectURL(url), 15000);
  };

  const saveOrder = async () => {
    // Responsable de la muestra obligatorio (la base valida de nuevo y normaliza el nombre).
    const rt = muestra.responsable_tipo;
    if (!rt || (rt === 'personal' && !muestra.responsable_id)
        || (rt === 'externo' && (muestra.responsableOtro || '').trim().length < 3)) {
      alert('Falta el responsable de la muestra en el paso "Muestra".');
      return;
    }
    // Crédito EXIGE institución: la factura se carga a su cuenta (facturación mensual).
    if (factura.metodo === 'Crédito' && !institucionSel) {
      alert('El método "Crédito" requiere seleccionar una institución en el paso "4. Médico".');
      return;
    }
    try {
      // El No. de orden lo asigna la base al insertar (trigger trg_a_correlativo,
      // Lote 6 de la auditoría): dos sedes que guardan a la vez reciben números
      // seguidos y, si el guardado falla, el contador no avanza.

      // Paso 3: resolver-o-crear identidad estable del paciente.
      // El jsonb `paciente` se mantiene como snapshot histórico (inmutable por orden);
      // `paciente_id` enlaza a la tabla pacientes. Si el DPI existe, actualiza la ficha
      // con los datos no vacíos del form (el operador ya los confirmó al capturar).
      let pacienteId = null;
      try {
        const res = await resolverOCrearPaciente(paciente);
        pacienteId = res.id;
      } catch (ePac) {
        console.error('No se pudo resolver el paciente:', ePac);
        alert('Aviso: no se pudo enlazar el paciente a su expediente, pero la orden se guardará. Detalle: ' + (ePac?.message || ePac));
      }

      const record = {
        saved_by:      user.username,
        saved_by_name: user.name,
        estado:        'pendiente',
        // examenesNeto: mismo neto por línea que alimenta los totales mostrados y la
        // contraseña. Se guarda price=neto (convenio aplicado) y precioOriginal=lista.
        paciente, examenes: examenesNeto, muestra, medico, factura,
        paciente_id:   pacienteId,                    // Paso 3: FK a pacientes (snapshot sigue en `paciente`)
        total:         totalFinal,
        descuento,
        institucion_id: institucionSel?.id ?? null,   // Etapa 2: pertenencia/visibilidad/crédito (solo convenios)
        es_emergencia: esEmergencia,
        cargo_emergencia: esEmergencia ? 150 : 0,
        created_at:    toGT().toISOString(),
      };
      const saved = await insertRecord(record);
      const corrActual = saved.correlativo;

      // Fase 4.1A: si esta orden nació de una preorden del portal médico,
      // cerramos el ciclo: estado_lab='orden_creada' + correlativo (write-back).
      // No bloquea el guardado si falla: la orden ya existe, la marca es trazabilidad.
      if (preordenActiva) {
        try { await marcarPreorden(preordenActiva.id, 'orden_creada', corrActual); }
        catch (eMp) { console.error('No se pudo marcar la preorden:', eMp); }
      }

      // Fase 4.1C: reconciliación de identidad ficha del médico ↔ expediente del lab.
      // Ruta preorden (cadena de custodia) si hay preordenActiva; si no, match por
      // (médico referente, DPI). Best-effort: si falla, la orden ya vive, solo se loguea.
      if (pacienteId) {
        try {
          const vin = await vincularPacienteLab({
            pacienteId,
            solicitudId:  preordenActiva?.id ?? null,
            medicoCodigo: medico?.codigo ?? null,
          });
          if (vin?.estado) console.log('4.1C vínculo:', vin);
        } catch (eVin) {
          console.error('Vínculo ficha↔expediente no aplicado:', eVin);
        }
      }

      const nc = (await getCorrelativo()) || nextCorr(corrActual);
      setCorr(nc);
      setRecords(prev => [saved, ...prev]);

      let pwGenerada = null;
      if (generaContrasena) {
        const corrC  = await getCorrContrasena();

        // Misma derivación por línea que alimenta total/registro → coincide al centavo.
        const examenesDoc     = examenesNeto;
        const cargoEmergencia = esEmergencia ? 150 : 0;
        const descMonto       = descuento;                                  // = totalBruto − totalNeto
        const totalPw         = parseFloat((totalNeto + cargoEmergencia).toFixed(2));

        const pw = await insertPassword({
          correlativo: corrC,
          paciente:    `${paciente.nombre} ${paciente.apellido}`.trim(),
          habitacion:  paciente.habitacion,
          institucion: institucionSel?.nombre ?? null,
          examenes:    examenesDoc,
          total:       totalPw,
          descuento:        descMonto,
          es_emergencia:    esEmergencia,
          cargo_emergencia: cargoEmergencia,
          orden_corr:  corrActual,
          estado:      'activa',
          created_by:  user.username,
          created_at:  new Date().toISOString(),
        });

        const nextC = nextCorrContrasena(corrC);
        await setCorrContrasena(nextC);
        setCorrContrasena2(nextC);
        setPasswords(prev => [pw, ...prev]);
        pwGenerada = pw;
      }

      resetForm();

      const descTxt = pwGenerada
        ? `\n🔐 Contraseña: ${pwGenerada.correlativo}${hasDescuento ? ` (${descConvenioPct}% desc.)` : ' (sin desc.)'}`
        : '';
      alert(`✓ Orden ${corrActual} guardada.\nPróximo correlativo: ${nc}${descTxt}`);

      if (pwGenerada) {
        const imprimir = window.confirm('¿Desea imprimir la contraseña ahora?');
        if (imprimir) imprimirContrasena(pwGenerada);
      }

      // El formulario ya se limpió: sale sin preguntar.
      setActiveModule('registros', { sinConfirmar: true });
      setTab('paciente');

    } catch(e) {
      alert('Error al guardar: ' + e.message);
    }
  };

  const isAdmin    = user?.role === 'admin';
  const isQB       = user?.role === 'qb';
  const isMedico   = user?.role === 'medico';
  const isSecretaria = user?.role === 'secretaria';   // fase 2: solo agenda
  const isManager  = user?.role === 'manager';        // fase 3: administrador de una institución
  const isEmbriologa = user?.role === 'embriologa';  // Fertility · laboratorio de embriología
  const isSocio    = user?.role === 'socio';          // inversionista · solo informes financieros
  const isOperador = user?.role === 'op';   // FIX A
  const isStaff    = esStaff(user?.role);   // personal del laboratorio (admin/qb/op)

  // ── Módulo que REALMENTE se pinta ───────────────────────────────────────────
  // Derivado, nunca el estado crudo. `activeModule` es a dónde quiso ir el
  // usuario; `modulo` es a dónde se le permite llegar. Al derivarlo en el render
  // no existe ningún cuadro intermedio con la pantalla equivocada, y un estado
  // que quedó de antes (o un rol que cambió) cae solo en el módulo inicial del rol.
  // Los tres candados (rol, módulo de la clínica, permiso) se evalúan juntos con
  // `puedeEntrar` del registro (src/modulos.js), el mismo que usa el menú.
  // Con el enrutador, lo pedido sale de la dirección. Dos ajustes antes de los
  // candados: /consulta sin una consulta abierta lleva a Mis Pacientes (el id del
  // paciente en la dirección es de la etapa 3). Mientras la guardia de salida
  // pregunta, la dirección leída ya es la protegida (ver `rutaRetenida`).
  // /consulta/:id abre (o reabre al recargar) la consulta de ese paciente; un
  // id con formato malo muestra «sin acceso». /consulta a secas sin consulta
  // abierta sigue llevando a Mis Pacientes.
  const enConsulta      = activeModule === 'medico-workspace';
  const idConsultaUrl   = enConsulta ? rutaLeida.registroId : null;
  const consultaInvalida = enConsulta && rutaLeida.idInvalido;
  const pedido = (enConsulta && !wsAbierto && !idConsultaUrl && !consultaInvalida) ? 'medico-expediente'
    : activeModule;
  const accesoListo = !!user && permisosDe === user.id && modulosDe === user.id;
  const modulo = !accesoListo ? ''
    : (pedido && puedeEntrar(pedido, user, modulos, permisos))
      ? pedido
      : moduloInicial(user, modulos, permisos);

  // Si lo pintado no coincide con la dirección ("/", ruta desconocida, sin
  // permiso, módulo apagado en la clínica, /consulta sin consulta), se corrige
  // la DIRECCIÓN con replace. Nunca al revés: el candado ya se evaluó al pintar.
  // Se conservan query y hash (?google=, tokens de Supabase) tal como estén en
  // ese momento: ningún ajuste de dirección se los come.
  useEffect(() => {
    if (!modulo || rutaRetenida) return;
    const leida = leerRuta(location.pathname);
    // La consulta abierta va en su dirección: /consulta → /consulta/:id.
    const faltaIdConsulta = modulo === 'medico-workspace' && wsPacienteId
      && !leida.registroId && !leida.idInvalido;
    // Una ruta vieja (/qb/ordenes) se cambia por la actual del módulo.
    if (leida.modulo === modulo && !faltaIdConsulta && !leida.vieja) return;
    navegarRef.current(
      { pathname: rutaDe(modulo, faltaIdConsulta ? wsPacienteId : null), search: window.location.search, hash: window.location.hash },
      { replace: true },
    );
  }, [modulo, location.pathname, rutaRetenida, wsPacienteId]);

  // La consulta sigue a su dirección: entrar o recargar en /consulta/:id la
  // abre; atrás/adelante entre dos consultas cambia de paciente. Moverse por
  // el menú NO la cierra (la dirección deja de ser /consulta, el anclaje queda).
  useLayoutEffect(() => {
    if (!isMedico || !idConsultaUrl) return;
    if (wsPacienteId !== idConsultaUrl) { setWsPacienteNombre(''); setWsPacienteId(idConsultaUrl); }
    if (!wsAbierto) setWsAbierto(true);
  }, [idConsultaUrl, isMedico]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ── Nueva orden: no perder lo capturado ─────────────────────────────────────
  // Con datos, salir por el menú, por "← Volver", con atrás o recargando pide
  // confirmación. Sin datos sale sin preguntar. Los pasos internos (Paciente,
  // Exámenes…) siguen en estado: no son direcciones todavía.
  const ordenConDatos = examenes.length > 0 || esEmergencia || !!institucionSel
    || seccionTocada(paciente, EMPTY_PACIENTE) || seccionTocada(muestra, EMPTY_MUESTRA)
    || seccionTocada(medico, EMPTY_MEDICO) || seccionTocada(factura, EMPTY_FACTURA);
  const protegerOrden = modulo === 'nueva-orden' && ordenConDatos;
  // Menú, «← Volver», atrás/adelante y recargar: la guardia común
  // (src/utils/guardiaSalida.js), la misma del Ingreso de resultados.
  useGuardiaSalida(protegerOrden, MSG_SALIR_ORDEN);

  // Atrás / adelante con una guardia activa: la dirección ya cambió cuando el
  // navegador avisa, pero se sigue pintando la protegida (`rutaRetenida`). Aquí
  // se pregunta; si no se confirma, se deshace el salto con history.go. Corre
  // antes de pintar, así que no hay parpadeo.
  const idxPintado = useRef(null);          // idx del historial de lo último pintado sin retener
  useLayoutEffect(() => {
    if (!rutaRetenida) return;
    if (window.confirm(guardia.mensaje)) { setSalidaPermitida(location.key); return; }
    const idx = window.history.state?.idx;
    const previo = idxPintado.current;
    const salto = (typeof previo === 'number' && typeof idx === 'number') ? previo - idx : 1;
    window.history.go(salto || 1);
  }, [location.key]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Después de cada pintado normal se anota dónde quedó el historial: es a
  // donde vuelve el salto deshecho.
  useLayoutEffect(() => {
    if (!rutaRetenida) idxPintado.current = window.history.state?.idx;
  });


  // Orden importante: primero se tapa la pantalla, después se cierra la sesión y
  // recién al final se lleva la dirección a "/". Si se mueve antes, React
  // repinta con `session`/`user` todavía puestos y muestra otra pantalla a quien
  // no le corresponde. La sesión se limpia en local sin
  // esperar el evento SIGNED_OUT: si la red falla, el usuario igual queda fuera.
  const handleLogout = useCallback(async (opc) => {
    // opc llega como evento del clic cuando lo llama un botón: solo se usa .aviso.
    const aviso = (opc && typeof opc.aviso === 'string') ? opc.aviso : '';
    saliendoRef.current = true;
    sesionViva.current  = false;
    setSaliendo(true);
    try {
      await Promise.race([
        signOut(),
        new Promise(res => setTimeout(res, 4000)),   // que un timeout no deje la pantalla trabada
      ]);
    } finally {
      // flushSync: la sesión ya vacía tiene que estar pintada ANTES de mover la
      // dirección. Con una capa abierta (menú) el cambio a "/" llega después de
      // un paso atrás asíncrono, y si encontrara al usuario todavía puesto, la
      // corrección de dirección lo devolvería a su módulo.
      flushSync(() => {
        setSession(null);
        setUser(null);
        resetForm();
      });
      // "/" y no la ruta de un módulo: quien entre después aterriza en el suyo.
      navegarFuera({ pathname: '/' }, { replace: true });
      setAvisoSesion(aviso);
      setSaliendo(false);
      saliendoRef.current = false;
    }
  }, []);
  handleLogoutRef.current = handleLogout;
  // 1 hora: una primera consulta de fertilidad pasa de los 45 minutos y el
  // médico casi no toca la computadora mientras dicta. Con 30 minutos el
  // auto-logout lo sacaba a media grabación.
  useAutoLogout(user ? handleLogout : () => {}, 60 * 60 * 1000);

  // Fase 4.1A: pre-llena la orden desde una preorden (bandeja hoy, lector QR en 4.1B).
  // Traduce: med_pacientes → shape de TabPaciente; solicitud_items → catálogo del lab
  // (match por exam_id, fallback por nombre normalizado); medico_codigo → doctors.
  const precargarOrdenDesdeSolicitud = (pre) => {
    const norm = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

    // 1) Paciente (sexo normalizado al formato del select: Masculino/Femenino)
    const p = pre.paciente || {};
    const sx = norm(p.sexo);
    const upd = {
      ...EMPTY_PACIENTE,
      nombre: p.nombre || '', apellido: p.apellido || '',
      nacimiento: p.nacimiento || '', telefono: p.telefono || '', dpi: p.dpi || '',
      sexo: sx === 'm' || sx === 'masculino' ? 'Masculino' : sx === 'f' || sx === 'femenino' ? 'Femenino' : '',
    };
    if (upd.nacimiento) {
      const diff = Date.now() - new Date(upd.nacimiento).getTime();
      upd.edad_dias = Math.floor(diff / (24 * 3600 * 1000));
      upd.edad = Math.floor(diff / (365.25 * 24 * 3600 * 1000)).toString();
    }
    setPaciente(upd);

    // 2) Exámenes: buscar cada renglón en el catálogo del lab (precio de lista).
    //    Lo que no matchea entra con precio 0 y se avisa al operador para cotizarlo.
    const catalogo = sharedCatalog || [];
    const sinPrecio = [];
    const sel = (pre.items || []).map((it, idx) => {
      const hit = catalogo.find(c => String(c.id) === String(it.exam_id))
               || catalogo.find(c => norm(c.name) === norm(it.exam_name));
      if (hit) return { ...hit, precioOriginal: hit.price, price: hit.price };
      sinPrecio.push(it.exam_name);
      return { id: Date.now() + idx, cat: it.categoria || 'Otros', name: it.exam_name, precioOriginal: 0, price: 0 };
    }).filter((e, i, arr) =>
      // Sin duplicados: el mismo examen se cobra una sola vez.
      arr.findIndex(x => String(x.id) === String(e.id) || norm(x.name) === norm(e.name)) === i
    );
    setExamenes(sel);

    // 3) Médico: por código contra el directorio; si no está, snapshot de la solicitud.
    const doc = doctors.find(d => d.codigo === pre.medico_codigo);
    const m = pre.medico || {};
    setMedico(doc ? {
      nombre: doc.nombre, apellido: doc.apellido, telefono: doc.telefono || '',
      correo: doc.correo || '', colegiado: doc.colegiado || '',
      observaciones: doc.observaciones || '', codigo: doc.codigo || '', descuento: doc.descuento || 0,
    } : {
      nombre: m.nombre || '', apellido: m.apellido || '', telefono: m.telefono || '',
      correo: m.correo || '', colegiado: m.colegiado || '',
      observaciones: m.observaciones || '', codigo: pre.medico_codigo || '', descuento: m.descuento || 0,
    });

    // 4) Vincular la preorden y navegar al flujo de orden.
    //    4.1B: si los datos vienen del PAPEL (fallback del lector QR: sin conexión
    //    o solicitud no encontrada en la base), pre.id es null → NO se vincula
    //    preorden y saveOrder no intenta el write-back marcar_preorden.
    setPreordenActiva(pre.id ? { id: pre.id, codigo: pre.codigo } : null);
    setActiveModule('nueva-orden');
    setTab('paciente');

    if (pre.origen_papel) {
      alert('📄 Datos cargados desde el PAPEL (QR), no desde la base.\nVerifica nombre, DPI y teléfono con el paciente antes de guardar la orden.');
    }

    if (sinPrecio.length) {
      alert('⚠ Estos exámenes no están en el catálogo y entraron con precio Q0 (revisa en "2. Exámenes"):\n\n· ' + sinPrecio.join('\n· '));
    }
  };

  // Abre el workspace de consulta anclado a un med_paciente (desde cualquier puerta).
  const onOpenWorkspace = useCallback((medPacienteId) => {
    setWsPacienteId(prev => {
      // Reabrir el MISMO expediente no debe reiniciar la consulta: el workspace
      // sigue montado con su borrador y su dictado intactos, así que se conserva
      // la identidad del estado y solo se cambia de vista.
      if (prev && medPacienteId && prev === medPacienteId) return prev;
      setWsPacienteNombre('');
      return medPacienteId || null;
    });
    setWsAbierto(true);
    setActiveModule('medico-workspace', { registroId: medPacienteId || null });
  }, [setActiveModule]);

  // Dentro de la consulta el médico cambió de paciente (expediente vinculado o
  // el selector sin anclaje): la dirección lo sigue sin apilar otra entrada.
  const onCambioPacienteConsulta = useCallback((id) => {
    if (!id) return;
    setWsPacienteId(id);
    if (moduloDeRuta(rutaActual.current) !== 'medico-workspace') return;
    navegarFuera(rutaDe('medico-workspace', id), { replace: true });
  }, []);

  // ── Registros con dirección propia (etapa 3) ────────────────────────────────
  // Las pantallas `conId` abren y cierran su registro con estas dos funciones;
  // nunca arman la dirección a mano. Abrir desde la lista apila una entrada
  // (atrás vuelve a la lista); pasar de un registro a otro la reemplaza.
  // Cerrar: si se abrió desde la lista, se quita su entrada (como una capa);
  // si se entró directo por la dirección, se reemplaza por la lista.
  const abrirRegistro = useCallback((registroId) => {
    const l = ubicacionRef.current;
    const leida = leerRuta(l.pathname);
    if (!leida.modulo || !registroId) return;
    const destino = rutaDe(leida.modulo, registroId);
    if (destino === l.pathname) return;
    const hayRegistro = !!(leida.registroId || leida.idInvalido);
    const desdeLista = hayRegistro ? !!l.state?.desdeLista : true;
    navegarFuera(destino, { replace: hayRegistro, state: { desdeLista } });
  }, []);
  const cerrarRegistro = useCallback(() => {
    const l = ubicacionRef.current;
    const leida = leerRuta(l.pathname);
    if (!leida.modulo || !(leida.registroId || leida.idInvalido)) return;
    if (l.state?.desdeLista && !hayCapasAbiertas()) retrocederEnCola(1);
    else navegarFuera(rutaDe(leida.modulo), { replace: true });
  }, []);

  // Salida explícita de la consulta ("← Volver" del workspace). Es el ÚNICO
  // punto donde se suelta el anclaje: navegar a otro módulo del menú ya no
  // desmonta el expediente ni pierde lo que el médico llevaba escrito.
  const onCerrarWorkspace = useCallback(() => {
    setWsAbierto(false);
    setWsPacienteId(null);
    setWsPacienteNombre('');
    setActiveModule('medico-expediente');
  }, [setActiveModule]);

  // ── FIX B: badges diferenciados por rol ──────────────────────────────────────
  // QB/Admin: cuántos resultados "completado" esperan su aprobación
  const pendingResults  = results.filter(r => r.estado === 'completado').length;
  // Operador: cuántos de sus propios resultados fueron rechazados o van a control
  const rejectedResults = results.filter(
    r => (r.estado === 'rechazado' || r.estado === 'control') &&
         r.ingresado_por === user?.username
  ).length;
  // Todos: aprobados disponibles
  const approvedResults = results.filter(r => r.estado === 'aprobado').length;

  // ── FIX C: badge "pendiente distribuir" ──────────────────────────────────────
  // Correlativos aprobados que NO tienen ningún registro en distribution_log
  const distribuidosSet = new Set(distributionLogs.map(d => d.correlativo));
  const pendDistribuir  = results.filter(
    r => r.estado === 'aprobado' && !distribuidosSet.has(r.correlativo)
  ).length;

  const badges = {
    pending_results:   (isQB || isAdmin) ? (pendingResults  || null) : null, // Fix B
    rejected_results:  isOperador        ? (rejectedResults || null) : null, // Fix B
    approved_results:  approvedResults   || null,
    pend_distribuir:   (isQB || isAdmin) ? (pendDistribuir  || null) : null, // Fix C
    total_records:     records.length    || null,
  };

  const Splash = (txt) => (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh', background:C.navy, color:C.white, fontSize:12, letterSpacing:3, fontFamily:"'Inter','Segoe UI',system-ui,sans-serif" }}>
      {txt}
    </div>
  );

  // Splash con salida: todo estado de espera que puede fallar termina acá,
  // con un botón que reintenta y otro que deja salir.
  const SplashError = (txt, onRetry) => (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'100vh', background:C.navy, color:C.white, padding:24, textAlign:'center', fontFamily:"'Inter','Segoe UI',system-ui,sans-serif" }}>
      <div style={{ marginBottom:18 }}><InnovaLogo height={38} white /></div>
      <div style={{ fontSize:12, letterSpacing:2, fontWeight:'700', marginBottom:10 }}>NO SE PUDO CONECTAR</div>
      <div style={{ fontSize:12, color:'#cbd5e1', maxWidth:420, lineHeight:1.6, marginBottom:22 }}>{txt}</div>
      <button onClick={onRetry} style={{ ...g.btnS(C.teal), padding:'10px 26px', fontSize:12 }}>REINTENTAR</button>
      <button onClick={handleLogout} style={{ background:'none', border:'none', color:'#94a3b8', fontSize:11, marginTop:16, cursor:'pointer', textDecoration:'underline' }}>Cerrar sesión</button>
    </div>
  );

  // Entró por una dirección que no es la suya (un médico en operaciones, o
  // alguien del laboratorio en el portal). No se le pinta ningún módulo: se le
  // dice a dónde ir y se le deja salir.
  const OtraEntrada = (destino) => (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'100vh', background:C.navy, color:C.white, padding:24, textAlign:'center', fontFamily:"'Inter','Segoe UI',system-ui,sans-serif" }}>
      <div style={{ marginBottom:18 }}><InnovaLogo height={38} white /></div>
      <div style={{ fontSize:12, letterSpacing:2, fontWeight:'700', marginBottom:10 }}>ESTE ACCESO NO ES PARA TU USUARIO</div>
      <div style={{ fontSize:12, color:'#cbd5e1', maxWidth:420, lineHeight:1.6, marginBottom:22 }}>
        Estás en {ENTRADA.nombre}. Tu usuario entra por {destino.nombre}.
      </div>
      <a href={destino.url} style={{ ...g.btnS(C.teal), padding:'10px 26px', fontSize:12, textDecoration:'none' }}>IR A {destino.nombre.toUpperCase()}</a>
      <button onClick={handleLogout} style={{ background:'none', border:'none', color:'#94a3b8', fontSize:11, marginTop:16, cursor:'pointer', textDecoration:'underline' }}>Cerrar sesión</button>
    </div>
  );

  const avisoAuth = authCaido
    ? 'El servicio de sesiones no está respondiendo. Si al ingresar no pasa nada, espera unos minutos y vuelve a intentar.'
    : avisoSesion;

  if (!authReady)   return Splash('CONECTANDO...');
  if (saliendo)     return Splash('CERRANDO SESIÓN...');
  if (recoveryMode) return <RecoveryScreen onDone={() => setRecoveryMode(false)} />;
  if (!session)     return <Login aviso={avisoAuth} />;
  if (mfa.estado === 'pide')  return <MfaCodigo factorId={mfa.factorId} onSalir={handleLogout} onListo={() => setIntentoMfa(n => n + 1)} />;
  if (mfa.estado === 'error') return SplashError('No pudimos revisar tu verificación en dos pasos. Vuelve a intentar.', () => setIntentoMfa(n => n + 1));
  if (mfa.estado !== 'ok')    return Splash('VERIFICANDO SESIÓN...');
  if (mustChange)   return <ForceChangePassword userName={user?.name} onDone={() => {}} />;
  if (perfilError)  return SplashError(perfilError, () => { setPerfilError(''); setIntentoPerfil(n => n + 1); });
  if (!user)        return Splash('CARGANDO PERFIL...');
  if (!entradaPermite(user.role)) return OtraEntrada(entradaCorrecta(user.role));
  if (loading || !accesoListo) return Splash('CONECTANDO CON LA BASE DE DATOS...');
  if (loadError)    return SplashError(loadError, loadAll);
  // Usuario sin ningún módulo permitido (un rol nuevo en la base, por ejemplo):
  // no se le pinta ningún módulo. Preferimos una pantalla que lo diga
  // a una que le muestre de más.
  if (!modulo)      return SplashError('Tu usuario todavía no tiene módulos asignados. Contacta al laboratorio.', () => setIntentoPerfil(n => n + 1));

  const showOrderFlow = modulo === 'nueva-orden';
  // En el teléfono el título siempre dice algo: si el módulo no tiene uno
  // propio se usa la etiqueta con la que aparece en el menú.
  const entradaModulo = entradaMenu(modulo);
  const tituloPropio  = MODULOS[modulo]?.titulo || '';
  const tituloModulo  = showOrderFlow ? 'Nueva orden' : (tituloPropio || entradaModulo?.label || '');
  // El ícono del título es el mismo de su entrada en el menú (línea, no emoji).
  const IconoModulo   = SidebarIcons[entradaModulo?.icon] || null;
  const hayAvisos     = !!badges && Object.values(badges).some(Boolean);

  return (
    <div className={esMovil ? 'il-movil' : undefined} style={{ display:'flex', minHeight:'100vh', background:C.bg, fontFamily:"'Inter','Segoe UI',system-ui,sans-serif" }}>

      <Sidebar
        user={user}
        activeModule={modulo}
        onNavigate={(mod) => { if (!puedeEntrar(mod, user, modulos, permisos)) return; setActiveModule(mod); if (mod === 'nueva-orden') { setTab('paciente'); setPreordenActiva(null); } }}
        onNuevaCita={() => { setActiveModule('medico-clinica'); setNuevaCitaTrigger(Date.now()); }}
        consultaActiva={isMedico && wsAbierto ? { nombre: wsPacienteNombre } : null}
        onIrAConsulta={() => setActiveModule('medico-workspace')}
        onCerrarConsulta={onCerrarWorkspace}
        onLogout={handleLogout}
        badges={badges}
        permisos={permisos}
        modulos={modulos}
        esMovil={esMovil}
        abierto={menuAbierto}
        onCerrar={() => setMenuAbierto(false)}
      />

      {/* il-ops: personal del laboratorio. il-portal: portales de clínica
          (médico, secretaria, manager, embrióloga). Las dos solo tienen reglas
          en src/movil.css para teléfono; en computadora no cambian nada. */}
      <div className={isStaff ? 'il-ops' : (isMedico || isSecretaria || isManager || isEmbriologa || isSocio) ? 'il-portal' : undefined} style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0, overflow:esMovil ? 'visible' : 'hidden' }}>

        {/* Barra superior del teléfono: ☰ abre el menú y el título dice dónde
            está el usuario. Queda pegada arriba al desplazarse. */}
        {esMovil && (
          <div style={{
            position:'sticky', top:0, zIndex:900, flexShrink:0,
            background:C.navy, color:C.white,
            display:'flex', alignItems:'center', gap:8,
            padding:'6px 10px', paddingTop:'calc(6px + env(safe-area-inset-top))',
            boxShadow:'0 2px 10px rgba(0,0,0,0.25)',
          }}>
            <button
              onClick={() => setMenuAbierto(true)}
              aria-label="Abrir menú"
              style={{ position:'relative', width:44, height:44, flexShrink:0, border:'none', borderRadius:10, background:'rgba(255,255,255,0.08)', color:C.white, fontSize:22, lineHeight:1, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}
            >
              ☰
              {hayAvisos && <span style={{ position:'absolute', top:8, right:8, width:9, height:9, borderRadius:'50%', background:'#f97316', border:`2px solid ${C.navy}` }} />}
            </button>
            <div style={{ flex:1, minWidth:0, fontSize:15, fontWeight:'700', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', display:'flex', alignItems:'center', gap:8 }}>
              {IconoModulo && <IconoModulo size={17} style={{ color:'#8DB8FF', flexShrink:0 }} />}
              <span style={{ overflow:'hidden', textOverflow:'ellipsis' }}>{tituloModulo || 'Innovalab'}</span>
            </div>
            {showOrderFlow && (
              <button style={{ ...g.btn(C.white), color:C.white, borderColor:'rgba(255,255,255,0.5)', background:'transparent', fontSize:12, padding:'8px 12px', flexShrink:0 }} onClick={() => setActiveModule('registros')}>← Volver</button>
            )}
          </div>
        )}

        {showOrderFlow && (
          <>
            {!esMovil && <div style={g.topBar}>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <InnovaLogo height={32} white={true} />
                <div style={{ color:'#A9BDE0', fontSize:10, fontWeight:'600', letterSpacing:2, borderLeft:'1px solid #ffffff30', paddingLeft:12 }}>GESTIÓN DE ÓRDENES</div>
              </div>
              <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ textAlign:'right' }}>
                  <div style={{ color:C.white, fontSize:12 }}>{user.name}</div>
                  <div style={{ color:'#A9BDE0', fontSize:9, fontFamily:"'Courier New',monospace" }}>
                    {isAdmin ? 'ADMINISTRADOR' : isQB ? 'QUÍMICO BIÓLOGO' : isMedico ? 'MÉDICO' : isSecretaria ? 'SECRETARIA' : 'OPERADOR'}
                  </div>
                </div>
                <button style={{ ...g.btn(C.red), color:C.red, fontSize:10, padding:'4px 10px' }} onClick={() => setActiveModule('registros')}>← Volver</button>
              </div>
            </div>}

            <div style={g.tabs}>
              {TABS.map(t => (
                <button key={t.id} style={g.tab(tab === t.id)} onClick={() => setTab(t.id)}>{t.icon} {t.label}</button>
              ))}
            </div>

            <CorrBar
              corr={corr}
              setCorr={isAdmin ? handleSetCorr : () => {}}
              isAdmin={isAdmin}
              paciente={paciente}
              examenes={examenes}
              total={total}
              totalFinal={totalFinal}
              hasHabDescuento={hasDescuento}
              descuento={descuento}
              esEmergencia={esEmergencia}
            />
          </>
        )}

        {!showOrderFlow && !esMovil && (
          <div style={{ background:C.white, borderBottom:`1px solid ${C.border}`, padding:'12px 24px', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
            <div style={{ fontSize:15, fontWeight:'700', color:C.navy, display:'flex', alignItems:'center', gap:9 }}>
              {IconoModulo && tituloPropio && <IconoModulo size={18} style={{ color:C.teal, flexShrink:0 }} />}
              {tituloPropio}
            </div>
            <div style={{ fontSize:11, color:C.muted }}>
              {new Date().toLocaleDateString('es-GT', { weekday:'long', day:'2-digit', month:'long', year:'numeric' })}
              {' · '}
              <span style={{ color:C.teal, fontWeight:'600' }}>{isAdmin ? 'Admin' : isQB ? 'QB' : isMedico ? 'Médico' : isSecretaria ? 'Secretaria' : isManager ? 'Manager' : isSocio ? 'Socio' : 'Operador'}</span>
            </div>
          </div>
        )}

        <div style={{ flex:1, overflowY:esMovil ? 'visible' : 'auto', background:C.bg }}>
         <LimiteCarga>
          <Suspense fallback={<div style={{ padding:'40px 24px', color:C.muted, fontSize:12, letterSpacing:2 }}>CARGANDO...</div>}>
          {showOrderFlow && (
            <div style={esMovil ? { ...g.main, padding:'12px 12px 24px' } : g.main}>
              {tab === 'paciente'    && <TabPaciente    data={paciente} setData={setPaciente} esEmergencia={esEmergencia} setEsEmergencia={setEsEmergencia} onClear={clearPaciente} onBuscarDpi={buscarPacientePorDpi} />}
              {tab === 'examenes'    && <TabExamenes    examenes={examenes} setExamenes={setExamenes} total={total} totalFinal={totalFinal} descuento={descuento} descConvenioPct={descConvenioPct} paciente={paciente} isAdmin={isAdmin} sharedCatalog={sharedCatalog} setSharedCatalog={setSharedCatalog} sharedCatOrder={sharedCatOrder} setSharedCatOrder={setSharedCatOrder} esEmergencia={esEmergencia} />}
              {tab === 'muestra'     && <TabMuestra     data={muestra} setData={setMuestra} onClear={clearMuestra} />}
              {tab === 'medico'      && <TabMedico      data={medico} setData={setMedico} doctors={doctors} instituciones={instituciones.filter(i => i.es_convenio)} institucionSel={institucionSel} setInstitucionSel={setInstitucionSel} onClear={clearMedico} />}
              {tab === 'facturacion' && <TabFacturacion data={factura} setData={setFactura} total={total} totalFinal={totalFinal} descuento={descuento} generaContrasena={generaContrasena} hasDescuento={hasDescuento} institucionSel={institucionSel} examenes={examenes} paciente={paciente} onSave={saveOrder} esEmergencia={esEmergencia} onClear={clearFactura} />}
            </div>
          )}

          <div style={{ padding: showOrderFlow ? 0 : esMovil ? '12px 12px 24px' : '20px 24px' }}>
            {/* El workspace de consulta NO se desmonta al cambiar de módulo: se
                esconde. Un médico que salta a Ultrasonidos y vuelve encuentra su
                nota a medio escribir, su pestaña y su dictado tal como los dejó.
                Solo el "← Volver" del propio workspace suelta el expediente. */}
            {isMedico && modulo === 'medico-workspace' && consultaInvalida && (
              <SinAcceso onVolver={() => setActiveModule('medico-expediente')} textoVolver="Ir a Mis Pacientes" />
            )}
            {isMedico && (wsAbierto || idConsultaUrl) && (
              <div style={{ display: modulo === 'medico-workspace' && !consultaInvalida ? 'block' : 'none' }}>
                <TabWorkspaceMedico
                  currentUser={user}
                  medPacienteId={idConsultaUrl || wsPacienteId}
                  onVolver={onCerrarWorkspace}
                  onCambioPaciente={onCambioPacienteConsulta}
                  onPacienteEnFoco={setWsPacienteNombre}
                  catalog={sharedCatalog || []}
                />
              </div>
            )}
            {/* El resto de módulos: la pantalla que declara el registro. La key
                hace que cada módulo monte su propia pantalla aunque dos módulos
                usen el mismo componente (Hojas / Inventario / Control del
                laboratorio FIV, Resultados / Resultados completados). */}
            {MODULOS[modulo]?.vista && <Fragment key={modulo}>{MODULOS[modulo].vista({
              user, isAdmin, isQB, isMedico, isManager, permisos,
              records, setRecords, results, setResults,
              doctors, setDoctors, quotations, setQuotations,
              passwords, setPasswords, distributionLogs, setDistributionLogs,
              sharedCatalog, setSharedCatalog, sharedCatOrder, setSharedCatOrder,
              corr, handleSetCorr, corrContrasena, imprimirContrasena,
              guardarCorrContrasena: async (val) => { setCorrContrasena2(val); await setCorrContrasena(val); },
              navegar: setActiveModule,
              registroId: rutaLeida.modulo === modulo ? rutaLeida.registroId : null,
              registroInvalido: rutaLeida.modulo === modulo && rutaLeida.idInvalido,
              abrirRegistro, cerrarRegistro,
              onOpenWorkspace, nuevaCitaTrigger, setNuevaCitaTrigger,
              precargarOrdenDesdeSolicitud,
            })}</Fragment>}
          </div>
          </Suspense>
         </LimiteCarga>
        </div>
      </div>
    </div>
  );
}
